import { useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import { Pill } from '../../ui/DataBits';
import { errorText, rpc, type AppError } from '../admin/api';
import { StaffFrame, XsCell, type XsReport } from '../r3/common';
import { periodLabel, TOPICS7 } from '../../lib/r3format';
import { WhatWorksTable, type WhatWorks } from '../head/HeadR3Pages';
import { ThlPanel } from '../head/R3Panels';

// J4: the municipality coordinator's one screen. Complete quarters only,
// frozen once computed; schools that authorised and had at least ten
// answers; at least three of them; never one school.

type Report = {
  municipality_code: string;
  municipality_name: string;
  authorised_schools: number;
  min_schools: number;
  min_school_answers: number;
  quarters: XsReport[];
};

export function MunicipalityHome() {
  const { t } = useI18n();
  return (
    <StaffFrame roles={['municipality_coordinator']} title={t['r3.mu.title']}
      extraNav={[{ icon: '🏛️', label: t['r3.mu.nav'], href: '/municipality', active: true }]}>
      {() => <Body />}
    </StaffFrame>
  );
}

function Body() {
  const { t } = useI18n();
  const id = useId();
  const [r, setR] = useState<Report | null>(null);
  const [ww, setWw] = useState<WhatWorks | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  useEffect(() => {
    rpc<Report>('municipality_report', { p_quarters: 4 }).then((x) => (x.error ? setErr(x.error) : setR(x.data)));
    rpc<WhatWorks>('what_works').then((x) => !x.error && setWw(x.data));
  }, []);
  if (err) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!r) return <p role="status">{t.loading}</p>;
  const latest = r.quarters[0];
  return (
    <>
      <p className="notice notice-block">
        {t['r3.mu.intro'](r.municipality_name, r.authorised_schools, r.min_schools, r.min_school_answers)} <Pill kind="warn">{t['ts.draft']}</Pill>
      </p>
      <section className="panel p5" aria-labelledby={`${id}-q`}>
        <h2 className="panel-title" id={`${id}-q`}>{t['r3.mu.quarters']}</h2>
        <div className="table-wrap" tabIndex={0} role="region" aria-labelledby={`${id}-q`}>
          <table className="table">
            <caption className="visually-hidden">{t['r3.mu.quarters']}</caption>
            <thead>
              <tr>
                <th scope="col">{t['r3.mu.quarter']}</th>
                <th scope="col">{t['r3.mu.all']}</th>
                <th scope="col">{t['r3.stage.primary']}</th>
                <th scope="col">{t['r3.stage.lower_secondary']}</th>
                <th scope="col">{t['r3.stage.upper_secondary']}</th>
              </tr>
            </thead>
            <tbody>
              {r.quarters.map((q) => (
                <tr key={q.period}>
                  <th scope="row">{periodLabel(q.period)}</th>
                  <td><XsCell w={q.total} /></td>
                  <td><XsCell w={q.stages.primary} /></td>
                  <td><XsCell w={q.stages.lower_secondary} /></td>
                  <td><XsCell w={q.stages.upper_secondary} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="form-hint">{t['r3.mu.frozen']}</p>
      </section>
      {latest && <TopicShares report={latest} />}
      {ww && <WhatWorksTable data={ww} />}
      <ThlPanel />
    </>
  );
}

export function TopicShares({ report }: { report: XsReport }) {
  const { t } = useI18n();
  const id = useId();
  const s = report.total.status === 'ok' ? report.total.topic_shares : null;
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['r3.mu.topics'](periodLabel(report.period))}</h2>
      {!s ? (
        <p className="text-muted">{t['h2.windowHidden']}</p>
      ) : (
        <ul className="bars">
          {TOPICS7.map((k) => (
            <li className="bar-row" key={k}>
              <span className="bar-label">{t[`topic.${k}` as 'topic.exams']}</span>
              <span className="track" aria-hidden="true"><i style={{ width: `${s[k] ?? 0}%`, background: 'var(--teal)' }} /></span>
              <span className={`bar-count${s[k] == null ? ' hidden' : ''}`}>{s[k] == null ? t['results.hidden'] : `${s[k]} %`}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
